class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.backgroundState = [];
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-ss-cart-close], #CartDrawer-Overlay')) this.close();
    });
    this.setHeaderCartIconAccessibility();
    this.headerClick = (event) => {
      const link = event.target.closest('#cart-icon-bubble, [data-ss-cart-open]');
      if (!link || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(link);
    };
    this.headerKeydown = (event) => {
      const link = event.target.closest('#cart-icon-bubble, [data-ss-cart-open]');
      if (link && event.code === 'Space') { event.preventDefault(); this.open(link); }
    };
    document.addEventListener('click', this.headerClick);
    document.addEventListener('keydown', this.headerKeydown);
  }

  disconnectedCallback() {
    this.close();
    document.removeEventListener('click', this.headerClick);
    document.removeEventListener('keydown', this.headerKeydown);
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('#cart-icon-bubble, [data-ss-cart-open]').forEach((link) => {
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', 'CartDrawer');
      link.setAttribute('aria-expanded', this.classList.contains('active') ? 'true' : 'false');
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains('active')) return;
    if (!this.activeElement || !this.activeElement.isConnected) this.setActiveElement(document.activeElement);
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('animate', 'active');
    this.backgroundState = [];
    Array.from(document.body.children).forEach((element) => {
      if (element === this || element.contains(this) || /^(SCRIPT|STYLE|LINK)$/.test(element.tagName)) return;
      this.backgroundState.push([element, element.inert]);
      element.inert = true;
    });
    document.body.classList.add('ss-cart-open', 'overflow-hidden');
    this.setHeaderCartIconAccessibility();
    // Focus does not depend on a transition event, including reduced-motion mode.
    this.focusFrame = requestAnimationFrame(() => {
      if (!this.classList.contains('active')) return;
      trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
    });
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    if (!this.classList.contains('active')) return;
    cancelAnimationFrame(this.focusFrame);
    this.classList.remove('active');
    this.backgroundState.forEach(([element, wasInert]) => { element.inert = wasInert; });
    this.backgroundState = [];
    document.body.classList.remove('ss-cart-open', 'overflow-hidden');
    removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble'));
    this.setAttribute('aria-hidden', 'true');
    this.setHeaderCartIconAccessibility();
  }

  renderContents(parsedState, open = true) {
    const drawerHTML = parsedState.sections?.['cart-drawer'];
    if (!drawerHTML || !this.getSectionDOM(drawerHTML, '#CartDrawer')) {
      return this.refresh(open).catch((error) => {
        if (open) window.location.assign(window.SSWorld?.cartURL() || routes.cart_url);
        else throw error;
      });
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionHTML = parsedState.sections?.[section.id];
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const source = sectionHTML && this.getSectionDOM(sectionHTML, section.selector || '.shopify-section');
      if (target && source) target.innerHTML = source.innerHTML;
    });
    this.syncState();
    if (open && !this.classList.contains('active')) this.open();
    else if (this.classList.contains('active')) {
      trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
    }
  }

  async refresh(open = false) {
    const url = new URL(window.SSWorld?.sectionURL() || routes.cart_url, window.location.origin);
    url.searchParams.set('sections', this.getSectionsToRender().map((section) => section.id).join(','));
    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(window.cartStrings.error);
    const sections = await response.json();
    if (!sections['cart-drawer'] || !this.getSectionDOM(sections['cart-drawer'], '#CartDrawer')) throw new Error(window.cartStrings.error);
    this.renderContents({ sections }, open);
  }

  syncState(cartState) {
    const inner = this.querySelector('.drawer__inner');
    const count = Number(cartState?.item_count ?? inner?.dataset.itemCount ?? 0);
    if (inner) inner.dataset.itemCount = count;
    this.classList.toggle('is-empty', count === 0);
    document.querySelectorAll('.ss3-cart-count, [data-ss-cart-count]').forEach((element) => { element.textContent = count; });
    this.setHeaderCartIconAccessibility();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') { return this.getSectionDOM(html, selector)?.innerHTML || ''; }

  getSectionsToRender() {
    const sections = [{ id: 'cart-drawer', selector: '#CartDrawer' }];
    const icon = document.getElementById('cart-icon-bubble');
    // The custom world header owns its label and icon. Update only its counter.
    if (icon && !icon.classList.contains('ss3-cart')) sections.push({ id: 'cart-icon-bubble' });
    return sections;
  }

  getSectionDOM(html, selector = '.shopify-section') { return new DOMParser().parseFromString(html, 'text/html').querySelector(selector); }
  setActiveElement(element) { this.activeElement = element; }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }];
    const icon = document.getElementById('cart-icon-bubble');
    if (icon && !icon.classList.contains('ss3-cart')) sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    return sections;
  }

  onCartUpdate() {
    return this.closest('cart-drawer')?.refresh(false).catch(() => {
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = window.cartStrings.error;
    });
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);
